import { Button, Modal, notification, Tooltip } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { callProvince } from "../../../../../components/address/address.logic";
import {
    createFieldDate,
    createFieldInput,
    createFieldInputNumber,
    createFieldInputPassword,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import { dateFormat } from "../../../../../utils/date";
import { download } from "../../../../../utils/file";
import { conditionFormChecker, copyForm, putField } from "../../../../../utils/form";
import { callCheckIdCard } from "../../../../account/register/register.section";
import { callTitle } from "../../../setting/user-mgt/user/user.section";
import { createFieldDesc } from "../tab-1/tab1.section";

const Address = defineAsyncComponent(() => import("../../../../../components/address/Address"));
const AttachmentInlineList = defineAsyncComponent(() => import("../../../../../components/attachment/AttachmentInlineList"));
const CIcon = defineAsyncComponent(() => import("../../../../../components/icon/CIcon"));
const CSelect = defineAsyncComponent(() => import("../../../../../components/input/template-builder/select/CSelect"));
const CDatePicker = defineAsyncComponent(() => import("../../../../../components/input/template-builder/date-picker/CDatePicker"));

export const callDocumentTypePerson = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "DOCUMENT_TYPE_PERSON", value: searchQuery });
export const callUserRequestType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_TYPE", value: searchQuery });
export const callUserRequestDoc = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_DOC", value: searchQuery });
export const callUserStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_STATUS", value: searchQuery });
export const callNationality = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "NATIONALITY", value: searchQuery });
export const callRace = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "RACE", value: searchQuery });
export const callReligion = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "RELIGION", value: searchQuery });
export const callGender = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "GENDER", value: searchQuery });
export const callUserRequestDoc4Attachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_DOC_4_ATTACHMENT", value: searchQuery });
export const callUserRequestStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_STATUS", value: searchQuery });
export const callUser = ({ searchQuery, parentUsername }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refParent.username", type: "String", operator: "=", value: parentUsername }
    ];
    if (searchQuery) {
        where.push({ name: "fullNameTh", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                select: ["id", "fullNameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "fullNameTh", type: "ASC" }],
                children: []
            }
        })
        .then(page => page.data);
};
export const callUserFull = ({ id }) => {
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    {
                        name: "addresses",
                        select: ["**"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    { name: "attachments", select: ["**"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] }
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createColumns = () => {
    return [
        createColumnInput({ title: () => i18n.global.t("model.user.fullNameTh"), dataIndex: "refUserTemp.fullNameTh", width: 200 }),
        createColumnSelect({
            title: () => i18n.global.t("model.user.refDocumentTypePerson"),
            dataIndex: "refUserTemp.refDocumentTypePerson.value",
            inputProp: () => ({ list: callDocumentTypePerson, optLabel: "value" }),
            search: { name: "refUserTemp.refDocumentTypePerson.id" },
            align: "center",
            width: 170
        }),
        createColumnInput({ title: () => i18n.global.t("model.user.idCard"), dataIndex: "refUserTemp.idCard", align: "center", width: 130 }),
        createColumnInput({ title: () => i18n.global.t("model.user.email"), dataIndex: "refUserTemp.email", align: "center", width: 130 }),
        createColumnDate({
            title: () => i18n.global.t("views.company.mgt.tab.approveDate"),
            dataIndex: "lastStatus.approve.0.statusDatetime",
            align: "center",
            width: 130
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refRequestType"),
            dataIndex: "refRequestType.value",
            inputProp: () => ({ list: callUserRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" },
            align: "center",
            width: 110
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callUserRequestStatus, optLabel: "value" }),
            search: { name: "refStatus.id" },
            align: "center",
            width: 100
        })
    ];
};

export const createField = ({ props, data, refs, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            ...createFieldDesc({ props, data, key, isLoading }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userRequest.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refUser
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.userRequest.refRequestType")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./tab4.logic");
                                                cloneUser({
                                                    model: {
                                                        ...createForm({ props, data, refs }),
                                                        id: model.id,
                                                        version: model.version,
                                                        isActive: model.isActive,
                                                        refStatus: model.refStatus,
                                                        refRequestDoc: model.refRequestDoc
                                                    }
                                                });
                                            } else {
                                                cloneUser({ model, id: model.refUser?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: callUserRequestType,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refUser`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.company.mgt.tab.4.title"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refUser,
                            "onUpdate:value": value => {
                                const oldValue = model.refUser;
                                model.refUser = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("views.company.mgt.tab.4.title") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneUser({ model, id: value?.id }),
                                        onCancel: () => {
                                            model.refUser = oldValue;
                                        }
                                    });
                                } else {
                                    cloneUser({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callUser({ searchQuery, parentUsername: store.state.resource.auth.info.user.username }),
                            optLabel: "fullNameTh",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullNameTh`) }
            }),
            ...createFieldUser1({ props, data, key: `${prefix}refUserTemp`, isLoading }),
            ...createFieldUser2({
                props,
                data,
                key: `${prefix}refUserTemp`,
                isLoading,
                onLinkIdCard: onLinkIdCard({ props, data, refs, isLoading })
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => (
                    <Address
                        menuContext={menuContext}
                        viewOnly={viewOnly}
                        dataProp={data}
                        dataKey={`${prefix}refUserTemp.addresses.0`}
                        isLoading={isLoading.value}
                    ></Address>
                )
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refUserTemp.attachments`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "AttachmentInlineList" } })
                    );
                    return <AttachmentInlineList {...componentProp}></AttachmentInlineList>;
                }
            }
        ]
    });
};

export const createFieldUser1 = ({ props, data, key /* , isLoading */ }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}username`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.username"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.username,
                            "onUpdate:value": value => {
                                if (value) {
                                    model.username = value.trim()
                                } else {
                                    model.username = value
                                }
                            },
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputPassword({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}password`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.password"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.password,
                            "onUpdate:value": value => (model.password = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputPassword({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}passwordConfirm`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.passwordConfirm"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.passwordConfirm,
                            "onUpdate:value": value => (model.passwordConfirm = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldUser2 = ({ props, data, key /* , isLoading, onLinkIdCard */ }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDocumentTypePerson`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refDocumentTypePerson"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDocumentTypePerson,
                            "onUpdate:value": value => {
                                model.refDocumentTypePerson = value;
                                useForm.validate(`${prefix}idCard`);
                            },
                            disabled: viewOnly,
                            list: callDocumentTypePerson,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}idCard`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.idCard"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.idCard,
                            "onUpdate:value": value => (model.idCard = value),
                            disabled: viewOnly,
                            comma: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {}),
                            suffix: () => {
                                const forceNoShow = true;
                                if (forceNoShow || !data.linkIdCard?.idCard) {
                                    return null;
                                }
                                let icon = <CIcon class="text-success" file="check.svg"></CIcon>;
                                if (data.linkIdCard?.idCard !== model.idCard) {
                                    icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
                                }
                                return <Tooltip title={data.linkIdCard?.idCard}>{icon}</Tooltip>;
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}buttonCheckIdCard`,
                colProp: () => ({ md: 4 }),
                formItemRender: () => {
                    let manual = <div></div>;
                    if (["2"].includes(model.refPersonType?.key)) {
                        manual = (
                            <a
                                class="text-center"
                                href="javascript:void(0)"
                                onClick={() =>
                                    download({
                                        file: { name: i18n.global.t("views.register.linkageClient"), url: "/files/server/LinkageClient.exe" }
                                    })
                                }
                            >
                                ({i18n.global.t("views.register.downloadLinkageClient")})
                            </a>
                        );
                    }
                    return (
                        <>
                            <div>
                                <Button
                                    class="mt-auto mb-auto btn-primary"
                                    onClick={() => {
                                        setIsLoadingApp(true);
                                        callCheckIdCard({ idCard: model.idCard }).finally(() => {
                                            setTimeout(() => {
                                                setIsLoadingApp(false);
                                                if (!data.linkageReceive001 || !data.linkageReceive009) {
                                                    notification["error"]({ message: i18n.global.t("error.linkage.noResponse") });
                                                }
                                            }, 10000);
                                        });
                                    }}
                                >
                                    {i18n.global.t("views.register.buttonCheckIdCard")}
                                </Button>
                            </div>
                            {manual}
                        </>
                    );
                }
            },
            {
                visible: () => data.linkIdCard?.addresses?.length > 0,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.register.linkIdCardAddress"),
                inputRender: () => {
                    return (
                        <>
                            {data.linkIdCard?.addresses?.map((d, index) => (
                                <div class="text-danger">
                                    {data.linkIdCard?.addresses?.length > 1 ? index + 1 + "." : ""} {d.name} {d.addrTh} {d.tumbol} {d.amphur}{" "}
                                    {d.province} {d.tel}
                                </div>
                            ))}
                        </>
                    );
                }
            },
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refIssuedBy`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refIssuedBy"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refIssuedBy,
                            "onUpdate:value": value => (model.refIssuedBy = value),
                            disabled: viewOnly,
                            list: callProvince,
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}outDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.outDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        { value: model.outDate, "onUpdate:value": value => (model.outDate = value), disabled: viewOnly },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}expireDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.expireDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.expireDate,
                            "onUpdate:value": value => (model.expireDate = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}birthDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.birthDate"),
                inputRender: ({ field }) => {
                    const inputProp = lodash.merge(
                        { value: model.birthDate, "onUpdate:value": value => (model.birthDate = value), disabled: viewOnly },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.birthDate && data.linkIdCard?.birthDate?.length >= 0 && model.birthDate !== data.linkIdCard.birthDate) {
                        linkCompare = (
                            <>
                                {dateFormat({ text: model.birthDate, format: "DD/MM/YYYY" })}
                                <span className="text-danger">==&gt; {dateFormat({ text: data.linkIdCard.birthDate, format: "DD/MM/YYYY" })}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CDatePicker {...inputProp}></CDatePicker>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refNationality`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refNationality"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refNationality");
                    const inputProp = lodash.merge(
                        {
                            value: model.refNationality,
                            "onUpdate:value": value => (model.refNationality = value),
                            disabled: viewOnly,
                            list: callNationality,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (
                        model.refNationality &&
                        data.linkIdCard?.nationalityDesc?.length >= 0 &&
                        model.refNationality.value !== data.linkIdCard.nationalityDesc
                    ) {
                        linkCompare = (
                            <>
                                {model.refNationality.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.nationalityDesc}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRace`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refRace"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRace,
                            "onUpdate:value": value => (model.refRace = value),
                            disabled: viewOnly,
                            list: callRace,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refReligion`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refReligion"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refReligion,
                            "onUpdate:value": value => (model.refReligion = value),
                            disabled: viewOnly,
                            list: callReligion,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refGender`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refGender"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refGender");
                    const inputProp = lodash.merge(
                        {
                            value: model.refGender,
                            "onUpdate:value": value => (model.refGender = value),
                            disabled: viewOnly,
                            list: callGender,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.refGender && data.linkIdCard?.genderDesc?.length >= 0 && model.refGender.value !== data.linkIdCard.genderDesc) {
                        linkCompare = (
                            <>
                                {model.refGender.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.genderDesc}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTitle`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refTitle")} (TH)`,
                inputRender: ({ field }) => {
                    const placeholder = `${i18n.global.t("model.user.refTitle")} (TH)`;
                    const inputProp = lodash.merge(
                        {
                            value: model.refTitle,
                            "onUpdate:value": value => (model.refTitle = value),
                            disabled: viewOnly,
                            list: callTitle,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.refTitle && data.linkIdCard?.titleName?.length >= 0 && model.refTitle.value !== data.linkIdCard.titleName) {
                        linkCompare = (
                            <>
                                {model.refTitle.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.titleName}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTitle`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refTitle")} (EN)`,
                inputProp: () => ({
                    value: model.refTitle,
                    "onUpdate:value": value => (model.refTitle = value),
                    disabled: true,
                    list: callTitle,
                    optLabel: "value2"
                }),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}firstNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.firstNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.firstNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.firstNameTh,
                            "onUpdate:value": value => (model.firstNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.firstNameTh && data.linkIdCard?.firstNameTh?.length >= 0 && model.firstNameTh !== data.linkIdCard.firstNameTh) {
                        linkCompare = (
                            <>
                                {model.firstNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.firstNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}firstNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.firstNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.firstNameEn,
                            "onUpdate:value": value => (model.firstNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}middleNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.middleNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.middleNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.middleNameTh,
                            "onUpdate:value": value => (model.middleNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.middleNameTh && data.linkIdCard?.middleNameTh?.length >= 0 && model.middleNameTh !== data.linkIdCard.middleNameTh) {
                        linkCompare = (
                            <>
                                {model.middleNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.middleNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}middleNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.middleNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.middleNameEn,
                            "onUpdate:value": value => (model.middleNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.lastNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.lastNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.lastNameTh,
                            "onUpdate:value": value => (model.lastNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.lastNameTh && data.linkIdCard?.lastNameTh?.length >= 0 && model.lastNameTh !== data.linkIdCard.lastNameTh) {
                        linkCompare = (
                            <>
                                {model.lastNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.lastNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.lastNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastNameEn,
                            "onUpdate:value": value => (model.lastNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}mobile`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.mobile"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.mobile,
                            "onUpdate:value": value => (model.mobile = value),
                            disabled: viewOnly,
                            comma: false,
                            maxDecimal: 10
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const cloneUser = ({ model, id }) => {
    if (id) {
        callUserFull({ id }).then(user => {
            const refUserTemp = copyForm({ form: { ...user } });
            router.push({
                name: router.currentRoute.value.name,
                params: { props: JSON.stringify({ form: { ...model, refUserTemp } }) },
                query: { _timestamp: new Date().getTime() }
            });
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};

export const onLinkIdCard = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return d => {
        const result = [];
        if ((model.refUserTemp.idCard || "") !== (d?.idCard || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("views.register.idCard")
                })
            );
        if ((model.refUserTemp.birthDate || "") !== (d?.birthDate || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.birthDate")
                })
            );
        if ((model.refUserTemp.refNationality?.value || "") !== (d?.nationalityDesc || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.refNationality")
                })
            );
        if ((model.refUserTemp.refTitle?.value || "") !== (d?.titleName || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.refTitle")
                })
            );
        if ((model.refUserTemp.firstNameTh || "") !== (d?.firstNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.firstNameTh")
                })
            );
        if ((model.refUserTemp.middleNameTh || "") !== (d?.middleNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.middleNameTh")
                })
            );
        if ((model.refUserTemp.lastNameTh || "") !== (d?.lastNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.lastNameTh")
                })
            );
        model.lastStatus.forOfficeRemark[0].remark = result.join("\r\n");
    };
};
